/**
 * Registry of partners using the /generic/* sample-request flow.
 *
 * To onboard a new partner: add a config file in this directory and register it
 * in GENERIC_PARTNERS below. No component edits needed.
 *
 * Note: Medvantx is intentionally NOT registered here — it lives on the legacy
 * /medvantx/* endpoints and Pages/SampleOrderForm/* components.
 */

import type { OrderItem } from "../../api/samplesIntegrationApi";
import type { PartnerConfig, PartnerCopy } from "./types";
import caretria from "./caretria";

export type { PartnerConfig, PartnerCopy, PartnerBranding, DrugConfig } from "./types";

export const GENERIC_PARTNERS: Record<string, PartnerConfig> = {
  [caretria.id]: caretria,
};

export function getPartnerConfig(partnerId: string | undefined | null): PartnerConfig | null {
  if (!partnerId) return null;
  return GENERIC_PARTNERS[partnerId] ?? null;
}

export function isGenericPartner(partnerId: string | undefined | null): boolean {
  return getPartnerConfig(partnerId) !== null;
}

/**
 * Resolve products for a (partner, drugName) pair. Falls back to whatever the
 * backend returned in decision.products if the drug isn't in the config.
 */
export function resolveDrugProducts(
  partnerId: string,
  drugName: string | undefined | null,
  fallbackProducts?: OrderItem[]
): OrderItem[] {
  const config = getPartnerConfig(partnerId);
  if (config && drugName && config.drugs[drugName]) {
    return config.drugs[drugName].products;
  }
  return fallbackProducts || [];
}

/**
 * Resolve copy for a (partner, drugName) pair: defaultCopy with per-drug overrides applied.
 */
export function resolveDrugCopy(
  partnerId: string,
  drugName: string | undefined | null
): PartnerCopy | null {
  const config = getPartnerConfig(partnerId);
  if (!config) return null;
  const override = drugName ? config.drugs[drugName]?.copy : undefined;
  return { ...config.defaultCopy, ...(override || {}) };
}
